import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Optional,
    Output,
    ViewChild,
} from '@angular/core';
import { OccupancyLimits, OccupancySelection } from './search-occupancy.model';
import { PopupOffcanvasComponent } from 'src/app/controls/popup-offcanvas/popup-offcanvas.component';
import { screenSizes } from '@app/utilities/theme';

@Component({
    selector: 'md-search-occupancy',
    templateUrl: './search-occupancy.component.html',
    styleUrls: ['./search-occupancy.component.scss'],
})
export class SearchOccupancyComponent implements OnInit {
    @Input() darkBackgroundOnOpen = true;
    @Input() showChildAgeLabel = true;
    @Input() set initialOccupancy(value: OccupancySelection) {
        if (value) {
            this.nrOfAdults = value.adults;
            this.nrOfChildren = value.children.length;
            this.childrenAges = value.children;
            this.childAgeOptions = this.fillAllAgesForEachChild();
        }
    }
    @Input() @Optional() set occupancyLimits(value: OccupancyLimits) {
        this.internalOccupancyLimits = value;
        this.canAddAdults = this.getCanAddAdults(value);
        this.canAddChildren = this.getCanAddChildren(value);
    }

    nrOfAdults = 2;
    nrOfChildren = 0;
    childrenAges: number[] = [];
    childAgeOptions: number[][] = [];
    hasRenderedAsMobile: boolean;

    canAddAdults = true;
    canAddChildren = true;
    internalOccupancyLimits: OccupancyLimits;

    @Output() occupancyChanged = new EventEmitter<OccupancySelection>();
    @Output() mobileOccupancyClosed = new EventEmitter<void>();

    @ViewChild('searchOccupancyOffcanvas') offcanvas: PopupOffcanvasComponent;

    constructor() {
        this.openOccupancy = this.openOccupancy.bind(this);
        this.applyOccupancy = this.applyOccupancy.bind(this);
        this.onModalContinueClick = this.onModalContinueClick.bind(this);
        this.resetToTwoAdults = this.resetToTwoAdults.bind(this);
    }

    onAdultNrChanged(nrOfAdults: number) {
        this.nrOfAdults = nrOfAdults;
        this.setCanAdd();
    }
    onChildNrChanged(nrOfChildren: number) {
        const nrIncreased = nrOfChildren > this.nrOfChildren;
        this.nrOfChildren = nrOfChildren;
        if (nrIncreased) {
            this.childrenAges.push(-1);
        } else {
            this.childrenAges.pop();
        }
        this.setCanAdd();
    }

    selectClicked(index: number) {
        if (this.childAgeOptions.length <= index) {
            this.childAgeOptions.push(this.computeAllowedAgesForChild(index));
        } else {
            this.childAgeOptions[index] =
                this.computeAllowedAgesForChild(index);
        }
    }

    ageSelected(age: string, index: number) {
        this.childrenAges[index] = Number(age); // Convert from string to number
    }

    openOccupancy() {
        this.offcanvas.open();
    }

    ngOnInit(): void {
        this.hasRenderedAsMobile = window.innerWidth < screenSizes.mobile;
    }

    applyOccupancy() {
        this.offcanvas.close();
        this.occupancyChanged.emit({
            adults: this.nrOfAdults,
            children: this.childrenAges,
        });
    }

    onModalContinueClick() {
        this.applyOccupancy();
        this.mobileOccupancyClosed.emit();
    }

    resetToTwoAdults() {
        this.nrOfAdults = 2;
        this.nrOfChildren = 0;
        this.childrenAges = [];
        this.setCanAdd();
    }

    invalidChildAges(): boolean {
        return this.childrenAges.some((age) => age < 0);
    }

    private fillAllAgesForEachChild(): number[][] {
        return Array.from({ length: this.nrOfChildren }, () => {
            return Array.from({ length: 18 }, (_, i) => i);
        });
    }

    private setCanAdd() {
        if (!this.internalOccupancyLimits) {
            return;
        }
        this.canAddAdults = this.getCanAddAdults(this.internalOccupancyLimits);
        this.canAddChildren = this.getCanAddChildren(
            this.internalOccupancyLimits
        );
    }

    private getCanAddAdults(occupancyLimits: OccupancyLimits): boolean {
        if (!occupancyLimits) {
            return false;
        }

        const adultsAgeGroup = occupancyLimits.ageGroups.find(
            (ageGroup) => ageGroup.ageGroupType === 'Adult'
        );
        const infantsAgeGroup = occupancyLimits.ageGroups.find(
            (ageGroup) => ageGroup.ageGroupType === 'Infant'
        );

        if (!adultsAgeGroup) {
            return false;
        }

        const infantAgeTo = infantsAgeGroup ? infantsAgeGroup.ageTo : 0;

        const nrOfChildrenNotInfants = this.childrenAges.filter(
            (age) => age > infantAgeTo
        ).length;

        return (
            this.nrOfAdults < adultsAgeGroup.maxPax &&
            this.nrOfAdults <
                occupancyLimits.maxGuestInclInfants - this.nrOfChildren &&
            this.nrOfAdults <
                occupancyLimits.maxPaxNoInfants - nrOfChildrenNotInfants
        );
    }

    private getCanAddChildren(occupancyLimits: OccupancyLimits): boolean {
        if (!occupancyLimits) {
            return false;
        }

        const nonAdultAgeGroups = occupancyLimits.ageGroups.filter(
            (ageGroup) => ageGroup.ageGroupType !== 'Adult'
        );

        if (nonAdultAgeGroups.length === 0) {
            return false;
        }

        const maxPaxNonAdults = nonAdultAgeGroups.reduce(
            (acc, ageGroup) => acc + ageGroup.maxPax,
            0
        );

        return (
            this.nrOfChildren < maxPaxNonAdults &&
            this.nrOfChildren <
                occupancyLimits.maxGuestInclInfants - this.nrOfAdults
        );
    }

    private computeAllowedAgesForChild(index: number): number[] {
        if (!this.internalOccupancyLimits) {
            return Array.from({ length: 18 }, (_, i) => i);
        }

        const agesForAllOtherChildren = this.childrenAges
            .filter((_, i) => i !== index)
            .map((age) => {
                return age;
            });
        const ageGroupsThatStillHavePax =
            this.internalOccupancyLimits.ageGroups.filter((ageGroup) => {
                const nrOfChildrenInAgeGroup = agesForAllOtherChildren.filter(
                    (age) => age >= ageGroup.ageFrom && age <= ageGroup.ageTo
                ).length;
                return nrOfChildrenInAgeGroup < ageGroup.maxPax;
            });

        const agesThatCanStillBePicked = ageGroupsThatStillHavePax
            .map((ageGroup) => {
                return Array.from(
                    { length: ageGroup.ageTo - ageGroup.ageFrom + 1 },
                    (_, i) => i + ageGroup.ageFrom
                );
            })
            .flat();

        const agesThatCanStillBePickedWithoutAdultAges =
            agesThatCanStillBePicked.filter((age) => {
                return (
                    age <
                    this.internalOccupancyLimits.ageGroups.find(
                        (ageGroup) => ageGroup.ageGroupType === 'Adult'
                    ).ageFrom
                );
            });

        return agesThatCanStillBePickedWithoutAdultAges.sort((a, b) => a - b);
    }
}
